import { Config } from '@/config';
import { startingLocale } from '@/util/i18n/get-starting-locale';
import { buildTenantMessages, expandTerminology } from '@/util/i18n/terminology/expand-terminology';
import Vue from 'vue';
import VueI18n from 'vue-i18n';

Vue.use(VueI18n);

// Tokenized locale messages captured before terminology expansion, so they can be
// re-expanded against a tenant overlay at any time (see applyTenantTerminology).
let rawTemplates = null;

export const i18n = initializeI18n();

function initializeI18n() {
  return new VueI18n({
    locale: startingLocale,
    fallbackLocale: Config.i18n.defaultLocale,
    silentFallbackWarn: true,
    messages: loadLocaleMessages(),
  });
}

function loadLocaleMessages() {
  const locales = require.context('@/locales', true, /[A-Za-z0-9-_]+\.json$/i);
  const messages = {};
  locales.keys().forEach((key) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\.json$/i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      if (!messages[locale]) {
        messages[locale] = {};
      }
      Object.assign(messages[locale], locales(key));
    }
  });

  Object.keys(messages).forEach((locale) => {
    const chartsFile = `${locale}-charts.json`;
    if (locales.keys().includes(`./${chartsFile}`)) {
      const chartsMessages = locales(`./${chartsFile}`);
      Object.assign(messages[locale], chartsMessages);
      delete messages[`${locale}-charts`];
    }
  });
  resolvePlaceholders(messages);
  // Snapshot the tokenized templates, then expand in place with the default lexicon so the
  // bootstrap output is unchanged. The snapshot is what applyTenantTerminology re-expands later.
  rawTemplates = JSON.parse(JSON.stringify(messages));
  expandTerminology(messages);
  return messages;
}

/**
 * Re-expands the kept templates against a tenant terminology overlay and pushes the result
 * into the live i18n instance. Call once the tenant config is known (after login/bootstrap).
 * `overlay` shape: { terms: { '<locale>': { '<term>': { gender, forms } } } }.
 * Pass null (or call resetTenantTerminology) to fall back to the default lexicon.
 */
export function applyTenantTerminology(overlay) {
  if (!rawTemplates) {
    return;
  }
  const terms = overlay && overlay.terms ? overlay.terms : null;
  const messages = buildTenantMessages(rawTemplates, terms);
  Object.keys(messages).forEach((locale) => {
    i18n.setLocaleMessage(locale, messages[locale]);
  });
}

export function resetTenantTerminology() {
  applyTenantTerminology(null);
}

function resolvePlaceholders(messages) {
  for (const locale in messages) {
    const message = messages[locale];
    const shared = message.shared;
    if (shared) {
      resolveLocale(message, shared);
    }
  }
}

// Function to recursively resolve placeholders within a locale
function resolveLocale(message, shared) {
  for (const key in message) {
    const value = message[key];
    if (typeof value === 'string' && value.includes('@shared.')) {
      const placeholderKey = value.includes('.') ? value.substring(value.indexOf('.') + 1) : value;
      message[key] = resolvePlaceholder(placeholderKey, shared);
    } else if (typeof value === 'object') {
      resolveLocale(value, shared);
    }
  }
}

// Function to resolve placeholder recursively
function resolvePlaceholder(key, shared) {
  const parts = key.split('.');
  let value = shared;
  parts.forEach((part) => {
    value = value[part];
  });
  return value;
}
